import NoData from '@root/components/common/NoData';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
// import { VulnerabilityCounts } from './VulnerabilityCounts';
import agent from '@root/agent';
import HeaderBarNew from '@root/components/HeaderBarNew';
// import TableSkeleton from '@root/components/table/skeleton';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import { useEnvironments, useSelectedEnvironments } from '@root/hooks/environments';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import useSortData from '@root/hooks/useSortData';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { useCallback, useMemo, useState } from 'react';
import SideOverlay from './Sideoverlay';
import { hostLevelColumns } from './columns';
import { toast } from 'sonner';
import { exportPipeline } from './exportPipeline';
import FilterComponent from '@root/components/FilterNew';
import { capitalizeFirstLetter } from '@root/utilities/string';
import { formatTimeFilter } from '@root/hooks/features';
import useTimeFilter from '@root/hooks/SelectTimeFilter';
// import { sbomColumns } from '';
// import FilterComponent from '@root/components/FilterNew';
const EXPORTED_COLS = ['account_id', 'cluster', 'node_name', 'instance_id', 'ami_id', 'vulnerabilities'];

const EXPORTED_HEADERNAME = ['Account ID', 'Cluster', 'Node', 'Instance ID', 'AMI ID', 'Vulnerabilities'];

const Host = () => {
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // mui counts pages from 0
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { cloudProvider } = useCloudProvider();
  const { timeFilter } = useTimeFilter();
  const allEnvironments = useEnvironments();
  const { selectedEnvironments } = useSelectedEnvironments();
  const debouncedSearchString = useDebouncedValue(searchString, 750);
  const { sortData, handleSortModelChange } = useSortData();
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    instance: [],
    node_name: [],
  });

  const environments = useMemo(() => {
    if (selectedEnvironments.length && cloudProvider !== 'all') {
      return selectedEnvironments.filter(environment => environment.cloudProvider === cloudProvider);
    } else if (cloudProvider === 'all') {
      return selectedEnvironments;
    }
    return allEnvironments;
  }, [selectedEnvironments, cloudProvider, allEnvironments]);

  const { data: clusterListData, isFetching: isFetchingClusterList } = useEnhancedQuery(
    ['get_cluster_list', `${environments.map(e => e.id)}`, timeFilter, cloudProvider],
    () =>
      agent.Kubernetes.get_cluster_list({
        environments: environments.map(e => e.id),
        clusters: [],
        date_filter: formatTimeFilter(timeFilter),
        cloud_provider: cloudProvider,
      }),
  );

  const clusterLists = useMemo(() => {
    if (isFetchingClusterList) return [];
    return clusterListData?.data?.map(item => item.cluster_name);
  }, [clusterListData?.data, isFetchingClusterList]);

  const { data: vulsFilter, isFetching: isFetchingFilterKeys } = useEnhancedQuery(
    ['get_node_vulns_filter', environments],
    () =>
      agent.Kubernetes.get_node_vulns_filter({
        environments: environments.map(item => item.id),
        clusters: clusterLists,
        node_name: null,
      }),
    {
      enabled: !isFetchingClusterList,
    },
  );

  const { data: hostlevelData, isFetching: isFetchingHostLevelData } = useEnhancedQuery(
    [
      'get_host_level_data',
      debouncedSearchString,
      selectedEnvironments.map(item => item.id),
      // selectedEnvironmentTags,
      currentPage,
      selectedFilters,
      pageSize,
      sortData,
    ],
    () =>
      agent.Kubernetes.get_node_level_details({
        // scope: selectedEnvironmentTags,
        environments: environments.map(item => item.id),
        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        clusters: [],
        instances: selectedFilters.instance,
        nodes: selectedFilters.node_name,
        search_query: debouncedSearchString,
        page_number: currentPage,
        rows_per_page: pageSize,
        export: false,
      }),
  );

  const propertyValues = useMemo(() => {
    if (isFetchingFilterKeys) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(vulsFilter?.data || {})?.map(([key, values]) => [
        // Transform key
        key
          ?.split('_')
          ?.map(word => capitalizeFirstLetter(word))
          ?.join(' '),

        // Transform array values
        values.map(value => value),
      ]),
    );
  }, [isFetchingFilterKeys, vulsFilter?.data]);

  const rows = useMemo(() => {
    if (isFetchingHostLevelData) return [];
    return (
      hostlevelData?.data?.map((item, idx) => ({
        ...item,
        id: idx + 1,
      })) ?? []
    );
  }, [hostlevelData?.data, isFetchingHostLevelData]);

  const totalRows = useMemo(() => {
    if (isFetchingHostLevelData) {
      return 0;
    }
    return hostlevelData?.pagintation?.total;
  }, [hostlevelData?.pagintation?.total, isFetchingHostLevelData]);

  const handleRowClick = useCallback(r => {
    setSelectedRow(r.row);
    setShowDetailsPanel(true);
  }, []);

  const handleCloseDetailsPanel = useCallback(() => {
    setShowDetailsPanel(false);
    setSelectedRow(null);
  }, []);

  const handleExportData = useCallback(async () => {
    toast.info('Exporting data...', {
      position: 'top-center',
    });

    try {
      const response = await agent.Kubernetes.get_node_level_details({
        // scope: selectedEnvironmentTags,
        environments: environments.map(item => item.id),
        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        clusters: [],
        nodes: selectedFilters.node_name,
        instances: selectedFilters.instance,
        search_query: debouncedSearchString,
        page_number: currentPage,
        rows_per_page: pageSize,
        export: true,
      });

      if (response?.data) {
        const data = response.data?.map(item => {
          const _item = {};
          for (const key of EXPORTED_COLS) {
            if (item[key]) {
              _item[key] = item[key];
            } else {
              _item[key] = '-';
            }
          }
          return {
            ..._item,

            vulnerabilities: JSON.stringify(_item['vulnerabilities']),
          };
        });

        exportPipeline(data, EXPORTED_COLS, EXPORTED_HEADERNAME, 'Kubernetes Host');
      }

      toast.success('Data exported successfully', {
        position: 'top-center',
      });
    } catch (err) {
      console.log(err);
      toast.error(`Failed to export data. ${err.message}`, {
        position: 'top-center',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironments, sortData, debouncedSearchString, currentPage, pageSize]);

  // const { selectedEnvironments } = useSelectedEnvironments();
  return (
    <div style={{ margin: '15px' }}>
      <HeaderBarNew
        // marginTop={'5px'}
        marginBottom={'13px'}
        title={''}
        searchBar
        searchBarValue={searchString}
        setOpenFilter={setOpenFilter}
        openFilter={false}
        exportButton={true}
        disableExportMenu
        onExportButtonClick={handleExportData}
        setSearchTerm={setSearchString}
        searchBarOnChange={e => setSearchString(e.target.value)}
      />{' '}
      <FilterComponent
        openFilter={openFilter}
        option={propertyValues}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
        // isKubernetes={true}
        defaultValue={{}}
        // zIndex={zIndex}
      />
      <MuiTable
        columns={hostLevelColumns}
        paginationMode={'server'}
        rows={rows}
        LoaderState={isFetchingHostLevelData}
        components={{
          NoRowsOverlay: () => <NoData message={'No Host level vulnerabilities present!'} />,
        }}
        sortingMode="server"
        handleSortModelChange={handleSortModelChange}
        onPageChange={setCurrentPage}
        total_size={totalRows}
        pageSize={pageSize}
        page={currentPage}
        onPageSizeChange={setPageSize}
        noRowsMessage={'No Host level vulnerabilities present!'}
        rowHeight={49.9}
        onRowClick={handleRowClick}
        isDynamicHeight={true}
      />
      {showDetailsPanel && (
        <SideOverlay
          key={showDetailsPanel.toString()}
          onClose={handleCloseDetailsPanel}
          selectedRow={selectedRow}
          type={'host_level'}
        />
      )}
    </div>
  );
};

export default Host;
